/**
 * SearchPanel Component
 * Track search interface with filters and results
 */

import { useState, useEffect, useMemo } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useTrackSearch } from '../../api/hooks/useTracks';
import type { TrackSearchParams } from '../../api/types';
import { SearchResultItem } from './SearchResultItem';

// Common genres for dropdown
const GENRES = [
  'pop', 'rock', 'hip-hop', 'electronic', 'jazz', 'classical',
  'country', 'r-n-b', 'indie', 'metal', 'folk', 'reggae'
];

/**
 * Debounce hook for search input
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function SearchPanel() {
  const [query, setQuery] = useState('');
  const [yearStart, setYearStart] = useState<number | undefined>();
  const [yearEnd, setYearEnd] = useState<number | undefined>();
  const [genre, setGenre] = useState<string>('');

  // Debounce search query (300ms)
  const debouncedQuery = useDebounce(query, 300);

  // Build search params
  const searchParams: TrackSearchParams = useMemo(() => ({
    q: debouncedQuery,
    year_start: yearStart,
    year_end: yearEnd,
    genre: genre || undefined,
    limit: 50,
  }), [debouncedQuery, yearStart, yearEnd, genre]);

  // Execute search
  const { data, isLoading, isError, error } = useTrackSearch(
    searchParams,
    debouncedQuery.length > 0
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search Input */}
      <div className="p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tracks..."
            className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-2">
          {/* Year Range */}
          <input
            type="number"
            value={yearStart ?? ''}
            onChange={(e) => setYearStart(e.target.value ? Number(e.target.value) : undefined)}
            placeholder="Year from"
            min={1900}
            max={new Date().getFullYear()}
            className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
          <input
            type="number"
            value={yearEnd ?? ''}
            onChange={(e) => setYearEnd(e.target.value ? Number(e.target.value) : undefined)}
            placeholder="Year to"
            min={1900}
            max={new Date().getFullYear()}
            className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />

          {/* Genre Dropdown */}
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="col-span-2 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="">All Genres</option>
            {GENRES.map((g) => (
              <option key={g} value={g}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
            <p className="text-sm text-zinc-500">Searching...</p>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-red-400 mb-2">Search failed</p>
            <p className="text-xs text-zinc-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && debouncedQuery.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-500">
              Enter a search query to find tracks
            </p>
          </div>
        )}

        {/* No Results */}
        {!isLoading && !isError && debouncedQuery.length > 0 && data?.tracks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-400 mb-1">No tracks found</p>
            <p className="text-xs text-zinc-500">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Results List */}
        {!isLoading && !isError && data && data.tracks.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs text-zinc-500 mb-2">
              {data.total} result{data.total !== 1 ? 's' : ''}
            </div>
            {data.tracks.map((track) => (
              <SearchResultItem key={track.id} track={track} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
